import React, {useRef, useState} from 'react';
import {Helmet} from 'react-helmet-async';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../css/frontend/request-membership-page.css';
import {GoogleCaptchaKey} from "../../config/client-config";

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://api.example.com';
const RECAPTCHA_SITE_KEY = GoogleCaptchaKey;

// const INTEREST_OPTIONS = [
//     { value: 'wood', label: 'Ξύλο' },
//     { value: 'electronics', label: 'Ηλεκτρονικά' },
//     { value: 'textiles', label: 'Υφάσματα' },
//     { value: '3d', label: '3D Printing' },
//     { value: 'bikes', label: 'Ποδήλατα' },
//     { value: 'community', label: 'Κοινότητα / Events' },
// ];

export default function Membership() {
    const {t} = useTranslation();
    const recaptchaRef = useRef(null);
    const [submitting, setSubmitting] = useState(false);
    const [successId, setSuccessId] = useState(null);

    const {register, handleSubmit, formState: {errors}, watch, reset} = useForm({
        defaultValues: {
            interests: [], labTier: 'none',
            labStatus: 'none', consent: false
        },
    });

    const consent = watch('consent');

    const onSubmit = async (form) => {
        setSubmitting(true);

        // 0) Fast env guard
        if (!RECAPTCHA_SITE_KEY) {
            toast.error("Missing RECAPTCHA site key (REACT_APP_RECAPTCHA_SITE_KEY).");
            setSubmitting(false);
            return;
        }
        if (!recaptchaRef?.current) {
            toast.error("reCAPTCHA is not ready. Please reload the page.");
            setSubmitting(false);
            return;
        }

        let recaptchaToken = null;

        try {
            recaptchaToken = await recaptchaRef.current.executeAsync();
            if (!recaptchaToken) throw new Error("Empty reCAPTCHA token");
        } catch (err) {
            console.error("reCAPTCHA error:", err);
            toast.error("reCAPTCHA failed. Refresh and try again.");
            recaptchaRef.current?.reset();
            setSubmitting(false);
            return;
        }

        const payload = {
            recaptchaToken,
            data: {
                name: form.name?.trim(),
                email: form.email?.trim(),
                phone: form.phone?.trim(),
                message: form.message?.trim(),
                interests: form.interests || [],
                consent: !!form.consent,
            },
        };

        // 1) Fetch with timeout
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 12000); // 12s

        try {
            const resp = await fetch(`${API_BASE}/api/submit/request-membership`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(payload),
                signal: ctrl.signal,
            });

            let json = null;
            try {
                json = await resp.json();
            } catch { /* non-JSON or empty */
            }

            if (!resp.ok) {
                const msg =
                    json?.message ||
                    (resp.status === 400 ? "Bad request" :
                        resp.status === 403 ? "Forbidden" :
                            resp.status === 429 ? "Too many requests" :
                                `HTTP ${resp.status}`);
                throw new Error(msg);
            }

            setSuccessId(json?.id);
            toast.success("Η αίτηση υποβλήθηκε!");
            reset({interests: [], consent: false});
        } catch (err) {
            if (err.name === "AbortError") {
                toast.error("Network timeout. Please try again.");
            } else {
                console.error("Submission error:", err);
                toast.error(err.message || "An unexpected error occurred :(");
            }
        } finally {
            clearTimeout(timer);
            recaptchaRef.current?.reset();
            setSubmitting(false);
        }
    };

    // // (Dev-only) Logs any *other* unhandled promise rejections to the console clearly:
    //     if (process.env.NODE_ENV === "development") {
    //         window.addEventListener("unhandledrejection", (e) => {
    //             console.error("UNHANDLED PROMISE REJECTION:", e?.reason);
    //         });
    //     }


    return (
        <>
            <Helmet>
                <title>{t('MEMBERSHIP_TITLE') || 'Αίτηση Μέλους | SpaceTime'}</title>
                <meta name="description"
                      content={t('MEMBERSHIP_META') || 'Κάνε αίτηση για συνδρομή στο SpaceTime Lab.'}/>
                <meta property="og:title" content="SpaceTime | Membership"/>
                <meta property="og:url" content="https://spacetime.community/membership"/>
            </Helmet>

            <div className="container membership-page">
                <ToastContainer/>

                <header className="membership-hero">
                    <h1>{t('MEMBERSHIP_H1') || 'Αίτηση Μέλους'}</h1>
                    <p className="muted">
                        {t('MEMBERSHIP_INTRO') || 'Συμπλήρωσε τη φόρμα και η ομάδα θα επικοινωνήσει μαζί σου.'}
                    </p>
                </header>
                <img src="/img/lab/woodworking_lathe2.jpg" alt={t("LAB_WOOD_ALT") || "Ξυλουργείο"}
                     className="lab-card-image"/>
                {!successId ? (
                    <form className="membership-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                        <div className="form-row">
                            <label>
                                {t('MEMBER_FULLNAME') || 'Ονοματεπώνυμο'} *
                                <input
                                    type="text"
                                    {...register('name', {required: true, maxLength: 140})}
                                    placeholder={t('PH_FULLNAME') || 'π.χ. Γιώργος Παπαδόπουλος'}
                                />
                            </label>
                            {errors.name && <span className="err">{t('ERR_REQUIRED') || 'Απαιτείται πεδίο.'}</span>}
                        </div>

                        <div className="form-row">
                            <label>
                                Email *
                                <input
                                    type="email"
                                    {...register('email', {
                                        required: true,
                                        pattern: /\S+@\S+\.\S+/,
                                    })}
                                    placeholder="you@example.com"
                                />
                            </label>
                            {errors.email && <span className="err">{t('ERR_EMAIL') || 'Μη έγκυρο email.'}</span>}
                        </div>

                        <div className="form-row">
                            <label>
                                {t('PHONE') || 'Τηλέφωνο'}
                                <input type="tel" {...register('phone', {maxLength: 40})} placeholder="69xxxxxxxx"/>
                            </label>
                        </div>

                        {/*<div className="form-row">*/}
                        {/*    <fieldset>*/}
                        {/*        <legend>{t('INTERESTS') || 'Ενδιαφέροντα'}</legend>*/}
                        {/*        <div className="chips">*/}
                        {/*            {INTEREST_OPTIONS.map((opt) => (*/}
                        {/*                <label className="chip" key={opt.value}>*/}
                        {/*                    <input type="checkbox" value={opt.value} {...register('interests')} />*/}
                        {/*                    <span>{opt.label}</span>*/}
                        {/*                </label>*/}
                        {/*            ))}*/}
                        {/*        </div>*/}
                        {/*    </fieldset>*/}
                        {/*</div>*/}

                        <div className="form-row">
                            <label>
                                {t('MESSAGE') || 'Μήνυμα'}
                                <textarea
                                    rows={4}
                                    {...register('message', {maxLength: 2000})}
                                    placeholder={t('PH_MESSAGE') || 'Λίγα λόγια για τα κίνητρά σου...'}
                                />
                            </label>
                        </div>

                        <div className="form-row consent">
                            <label>
                                <input type="checkbox" {...register('consent', {required: true})} />
                                {t('CONSENT_TEXT') || 'Έχω διαβάσει και αποδέχομαι την Πολιτική Απορρήτου.'}
                            </label>
                            {errors.consent && <span className="err">{t('ERR_CONSENT') || 'Απαιτείται αποδοχή.'}</span>}
                            <p className="muted">
                                ➡ {t('SEE')} <a href="/privacy" target="_blank"
                                                rel="noopener noreferrer">{t('PRIVACY_POLICY') || 'Πολιτική Απορρήτου'}</a>
                            </p>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="cta-btn primary" disabled={!consent || submitting}>
                                {submitting ? (t('SENDING') || 'Αποστολή...') : (t('SUBMIT') || 'Υποβολή')}
                            </button>
                        </div>

                        <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITE_KEY}/>
                    </form>
                ) : (
                    <div className="success-box">
                        <h2>{t('THANKS') || 'Ευχαριστούμε!'}</h2>
                        <p>
                            {t('MEMBERSHIP_SUCCESS') || 'Η αίτησή σου καταχωρήθηκε. Θα επικοινωνήσουμε σύντομα.'}
                        </p>
                        <p className="muted">#{successId}</p>
                        <button className="cta-btn" onClick={() => setSuccessId(null)}>
                            {t('SEND_ANOTHER') || 'Νέα αίτηση'}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}
